import axios from 'axios';
import { CLOUD9_API } from '../constants';


// --- API mutation broadcast -------------------------------------------------
// Lets features react to any successful write (POST/PUT/PATCH/DELETE) that goes
// through the queued API facade, without wiring a callback into every call site.
// Used e.g. by the stock item edit dialog to refetch the single item so its
// tabs always reflect fresh data after a mutation.
const apiMutationListeners = new Set();
const MUTATION_METHODS = new Set(['post', 'put', 'patch', 'delete']);

export function onApiMutationSuccess(listener) {
  if (typeof listener !== 'function') return () => {};
  apiMutationListeners.add(listener);
  return () => {
    apiMutationListeners.delete(listener);
  };
}

function notifyApiMutationSuccess(config, response) {
  const method = String(config?.method || '').toLowerCase();
  if (!MUTATION_METHODS.has(method)) return;
  apiMutationListeners.forEach((listener) => {
    // A listener throwing must never break the request pipeline.
    try {
      listener({ method, url: config?.url, config, response });
    } catch (e) {
      // no-op
    }
  });
}


// List of subdomains to rotate through
const subdomains = Array.from({ length: 1000 }, (_, i) => `${i + 1}`);

let currentSubdomainIndex = 0;

// Queue to hold requests
const requestQueue = [];
const BATCH_SIZE = 6;

// Function to get the next subdomain
function getNextSubdomain() {
  const subdomain = subdomains[currentSubdomainIndex];
  currentSubdomainIndex = (currentSubdomainIndex + 1) % subdomains.length;
  return subdomain;
}

// Function to process the queue
async function processQueue() {
  while (requestQueue.length > 0) {
    const batch = requestQueue.splice(0, BATCH_SIZE);
    // const subdomain = getNextSubdomain();
    // const baseURL = `https://${subdomain}.${CLOUD9_API_DYNAMIC}`;
    const baseURL = `${CLOUD9_API}`;

    // Create a new axios instance for this batch with the correct baseURL
    const axiosInstance = axios.create({ baseURL });

    // Apply interceptors and headers to the axios instance
    axiosInstance.interceptors.request.use(
      (req) => {
        const token = localStorage.getItem('access_token');
        req.headers['incoming-origin'] = incomingOrigin;
        req.headers.Authorization = `Bearer ${token}`;
        return req;
      },
      (err) => {
        throw err;
      }
    );

    // // Process each request in the batch
    // const requests = batch.map((request) => {
    //   return axiosInstance({
    //     method: request.config.method,
    //     url: request.config.url,
    //     data: request.config.data,
    //     headers: request.config.headers,
    //   })
    //     .then((response) => ({ success: true, response }))
    //     .catch((error) => ({ success: false, response: error }));
    // });

    // const results = await Promise.all(requests);
    // currentSubdomainIndex = 0
    // // Resolve or reject the promises for each request in the batch
    // batch.forEach((request, index) => {
    //   if (results[index].success) {
    //     request.resolve(results[index].response);
    //   } else {
    //     request.reject(results[index].response);
    //   }
    // });

    batch.forEach((request) => {
      axiosInstance({
        method: request.config.method,
        url: request.config.url,
        data: request.config.data,
        headers: request.config.headers,
        // Forwarded explicitly: this facade rebuilds the request from named fields, so anything it
        // does not list is dropped. `params` used to be one of them, which silently sent every
        // `.get(url, { params })` call with no query string at all — the caller saw a 200 holding
        // the endpoint's *default* result (e.g. supplier payments-due without `includePaid`, so
        // the Paid tab was always empty) and had no way to tell.
        params: request.config.params,
        // So a caller can cancel (e.g. a page's fetches when it unmounts).
        signal: request.config.signal,
      })
        .then((response) => {
          // Resolve the request as soon as it succeeds
          request.resolve(response);
          notifyApiMutationSuccess(request.config, response);
        })
        .catch((error) => {
          // Reject the request as soon as it fails
          request.reject(error);
        });
    });
  }
}

// Function to add a request to the queue
function addRequestToQueue(config) {
  return new Promise((resolve, reject) => {
    // Add the request to the queue with resolve and reject callbacks
    requestQueue.push({ config, resolve, reject });

    // Process the queue immediately if it reaches the batch size
    if (requestQueue.length >= BATCH_SIZE) {
      processQueue();
    } else {
      // Process the queue after a short delay if it doesn't reach the batch size
      setTimeout(() => {
        if (requestQueue.length > 0) {
          processQueue();
        }
      }, 100); // Adjust the delay as needed (e.g., 100ms)
    }
  });
}

// Override Axios methods to add requests to the queue
function createQueuedAxiosInstance(isPrivate = true) {
  return {
    request: (config) => {
      return addRequestToQueue({ ...config, isPrivate });
    },
    get: (url, config) => {
      return addRequestToQueue({ method: 'get', url, ...config, isPrivate });
    },
    post: (url, data, config) => {
      return addRequestToQueue({ method: 'post', url, data, ...config, isPrivate });
    },
    put: (url, data, config) => {
      return addRequestToQueue({ method: 'put', url, data, ...config, isPrivate });
    },
    patch: (url, data, config) => {
      return addRequestToQueue({ method: 'patch', url, data, ...config, isPrivate });
    },
    delete: (url, config) => {
      return addRequestToQueue({ method: 'delete', url, ...config, isPrivate });
    },
  };
}

// Replace existing Axios instances with queued versions
export const PrivateApiCall = createQueuedAxiosInstance(true);
export const PublicApiCall = createQueuedAxiosInstance(false);


/** Same key as the admin shell (resto-app-admin src/native/platform.js), which owns the value. */
const NATIVE_TENANT_KEY = 'cloudix:incoming-origin';

// Function to get the incoming origin
const getIncomingOrigin = () => {
  // Inside the Android / iOS app (Capacitor) the page is served from localhost, so the tenant is
  // the workspace the shell stored — this MFE runs in the shell's window and shares its storage.
  try {
    if (window.Capacitor?.isNativePlatform?.()) return (localStorage.getItem(NATIVE_TENANT_KEY) || '').trim();
  } catch (e) {
    // fall through to the hostname
  }
  const isProduction = process.env.NODE_ENV === 'production';
  if (isProduction) {
    const hostname = window.location.hostname;
    const parts = hostname.split('.');
    const index = parts.indexOf('admin');

    if (index === 1) {
      return parts[index - 1];
    } else {
      return ''; // No subdomain found
    }
  } else {
    return 'delivery'; // In development, always use 'delivery'
  }
};

export const incomingOrigin = getIncomingOrigin();